import { useCallback, useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import client from "../../../api";
import { defaultQueryParams, QueryKey } from "../../../utils/api";
import { hasPassedStatus } from "../../../utils/statuses";
import { isItem } from "./specificationState";
import type { StatusBadgeColors } from "../../components/StatusBadge";

// ============================================================================
// Edit locking
// ============================================================================

/**
 * Whether a condition's spec is locked from edits in the BOM and Grid views.
 *
 * - While a condition is being modified, only THAT condition is editable;
 *   every other condition (submitted or not) is locked.
 * - Otherwise, conditions already part of a submitted work-order group are
 *   locked until the user starts a modification.
 */
export const isConditionEditLocked = (
  conditionId: number,
  activeModifyConditionId: number | null,
  submittedConditionIds: Set<number>
): boolean =>
  activeModifyConditionId !== null
    ? conditionId !== activeModifyConditionId
    : submittedConditionIds.has(conditionId);

// ============================================================================
// Types
// ============================================================================

export type ConditionStatusEntry = {
  condition: FormspecCondition;
  workOrders: ConditionWorkOrdersResponse[];
};

export type SubmittedGroup = {
  groupId: string;
  status: WorkOrderStatus | null;
  createdAt: string;
  priority: number | null;
  testLocation: string | null;
  needByDate: Date | null;
  notes: string | null;
  entries: ConditionStatusEntry[];
};

/**
 * A single work-order group that a condition belongs to, paired with that
 * condition's tasks within the group. Under the many-to-many model a
 * condition may belong to more than one group, hence the array of these in
 * `ConditionWithGroups`.
 */
export type ConditionGroupRow = {
  group: SubmittedGroup;
  workOrders: ConditionWorkOrdersResponse[];
};

/**
 * Condition-centric view of submitted work-order groups. The `/conditions/tasks`
 * response is group-centric (group -> conditions); this inverts it so the
 * Committed+ UI can render one expandable block per condition with a row per
 * group the condition belongs to.
 */
export type ConditionWithGroups = {
  condition: FormspecCondition;
  rows: ConditionGroupRow[];
};

export type ConditionSubmissionDetails = {
  planned_start_date: string | null;
  priority: number | null;
  location: string | null;
  notes: string | null;
};

export const createDefaultConditionDetail = (): ConditionSubmissionDetails => ({
  planned_start_date: null,
  priority: 4,
  location: null,
  notes: null,
});

// ============================================================================
// Pure work-order status helpers (no React / store access)
// ============================================================================

export const formatGroupTimestamp = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

export const formatNeedByDate = (value: Date | string | null) => {
  if (!value) {
    return "—";
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString();
};

export const getStatusPresentation = (
  status?: string | null
): StatusBadgeColors => {
  const normalized = (status ?? "").trim().toLowerCase();
  if (normalized === "done") {
    return {
      backgroundColor: "#dff2e1",
      color: "#1b5e20",
      borderColor: "#b6e2bf",
    };
  }
  if (normalized === "canceled" || normalized === "cancelled") {
    return {
      backgroundColor: "#fde3e3",
      color: "#b71c1c",
      borderColor: "#f5b9b9",
    };
  }
  if (normalized === "modifying") {
    return {
      backgroundColor: "#CFD8DC",
      color: "#455A64",
    };
  }
  return {
    backgroundColor: "#e0e0e0",
    color: "#2f2f2f",
    borderColor: "#d5d5d5",
  };
};

export const getStatusText = (status?: string | null) => {
  const normalized = (status ?? "").trim();
  if (!normalized || normalized.toLowerCase() === "unknown") {
    return "REQUESTED";
  }
  return normalized.toUpperCase();
};

export const getWorkOrderLinkLabel = (url?: string | null) => {
  if (!url) {
    return "";
  }
  const trimmed = url.replace(/\/+$/, "");
  const lastSegment = trimmed.split("/").pop();
  return lastSegment && lastSegment.length > 0 ? lastSegment : trimmed;
};

// ============================================================================
// Condition grouping / derivation
// ============================================================================

export type ConditionStatusCounts = {
  wip: number;
  staged: number;
  committed: number;
};

type ConditionGroups = {
  submittedGroups: SubmittedGroup[];
  notSubmitted: ConditionStatusEntry[];
  staged: ConditionStatusEntry[];
  committedPlus: ConditionStatusEntry[];
  stagedConditionIds: Set<number>;
  statusCounts: ConditionStatusCounts;
};

/**
 * Count unique conditions across the given entry lists. Each condition is
 * counted once by `cell_condition_id`, regardless of how many cells,
 * replicates, or work orders it has, and regardless of appearing in multiple
 * submitted work-order groups.
 */
export const countUniqueConditions = (
  ...entryLists: ConditionStatusEntry[][]
): number => {
  const ids = new Set<number>();
  entryLists.forEach((entries) => {
    entries.forEach(({ condition }) => {
      ids.add(condition.cell_condition_id);
    });
  });
  return ids.size;
};

/**
 * Derive the four buckets StatusView renders from the raw conditions +
 * work-order groups. `submittedConditionIds` is lifted into Specification.tsx
 * so GridView and StatusView agree on what's "submitted".
 */
export const useConditionGroups = (
  conditions: FormspecCondition[] | undefined,
  workOrders: Record<string, ConditionWorkOrderGroupResponse> | undefined,
  submittedConditionIds: Set<number>
): ConditionGroups => {
  // Conditions that have staged cells (replicates > 0) but have not yet been
  // committed to a work order group.
  const stagedConditionIds = useMemo(() => {
    const ids = new Set<number>();
    if (!conditions) {
      return ids;
    }
    conditions.forEach((condition) => {
      if (
        condition.replicates > 0 &&
        !submittedConditionIds.has(condition.cell_condition_id)
      ) {
        ids.add(condition.cell_condition_id);
      }
    });
    return ids;
  }, [conditions, submittedConditionIds]);

  const groups = useMemo(() => {
    if (!conditions || conditions.length === 0) {
      return {
        submittedGroups: [] as SubmittedGroup[],
        notSubmitted: [] as ConditionStatusEntry[],
        staged: [] as ConditionStatusEntry[],
        committedPlus: [] as ConditionStatusEntry[],
      };
    }

    const conditionById = new Map(
      conditions.map((condition) => [
        String(condition.cell_condition_id),
        condition,
      ])
    );

    const parseTimestamp = (value: string) => {
      const time = new Date(value).getTime();
      return Number.isNaN(time) ? 0 : time;
    };

    const submittedGroups = (workOrders ? Object.entries(workOrders) : [])
      .map(([groupId, group]: [string, ConditionWorkOrderGroupResponse]) => {
        const entries: ConditionStatusEntry[] = [];
        const conditionEntries = group.conditions ?? {};

        Object.entries(conditionEntries).forEach(([conditionId, tasks]) => {
          const condition = conditionById.get(conditionId);
          if (!condition) {
            return;
          }

          entries.push({
            condition,
            workOrders: (tasks ?? []) as ConditionWorkOrdersResponse[],
          });
        });

        const groupEntry: SubmittedGroup = {
          groupId,
          status: group.status ?? null,
          createdAt: group.created_at,
          priority: group.priority ?? null,
          testLocation: group.test_location ?? null,
          needByDate: group.need_by_date ?? null,
          notes: group.notes ?? null,
          entries,
        };

        return groupEntry;
      })
      .filter((group) => group.entries.length > 0)
      .sort(
        (a, b) => parseTimestamp(a.createdAt) - parseTimestamp(b.createdAt)
      );

    const notSubmitted: ConditionStatusEntry[] = [];
    const staged: ConditionStatusEntry[] = [];
    const committedPlus: ConditionStatusEntry[] = [];
    conditions.forEach((condition) => {
      if (submittedConditionIds.has(condition.cell_condition_id)) {
        return;
      }
      if (stagedConditionIds.has(condition.cell_condition_id)) {
        // A condition belongs in the "Committed+" tab when at least one of its
        // cells has reached the committed status or beyond. `condition.status`
        // is the max cell status across the condition's cells.
        const isCommittedPlus =
          !!condition.status && hasPassedStatus("C", condition.status, true);
        if (isCommittedPlus) {
          committedPlus.push({ condition, workOrders: [] });
        } else {
          staged.push({ condition, workOrders: [] });
        }
      } else {
        notSubmitted.push({ condition, workOrders: [] });
      }
    });

    return { submittedGroups, notSubmitted, staged, committedPlus };
  }, [conditions, workOrders, submittedConditionIds, stagedConditionIds]);

  const statusCounts = useMemo<ConditionStatusCounts>(() => {
    const submittedEntries = groups.submittedGroups.map(
      (group) => group.entries
    );
    return {
      wip: countUniqueConditions(groups.notSubmitted),
      staged: countUniqueConditions(groups.staged),
      // Committed+ renders both the ordinary committed conditions and every
      // submitted work-order group's condition rows. Count each condition once.
      committed: countUniqueConditions(
        groups.committedPlus,
        ...submittedEntries
      ),
    };
  }, [groups]);

  return { ...groups, stagedConditionIds, statusCounts };
};

/**
 * Invert group-centric `SubmittedGroup[]` into a condition-centric list. Each
 * condition appears once (keyed by `cell_condition_id`) with a row per group
 * it belongs to. Group ordering (and hence row ordering within a condition) is
 * preserved from the input. Conditions first appear in the order their first
 * group is encountered.
 *
 * Supports the many-to-many relationship: a condition may show multiple rows
 * (one per group), and a group may contribute rows to multiple conditions.
 */
export const pivotSubmittedGroupsByCondition = (
  groups: SubmittedGroup[]
): ConditionWithGroups[] => {
  const byConditionId = new Map<number, ConditionWithGroups>();

  groups.forEach((group) => {
    group.entries.forEach(({ condition, workOrders }) => {
      const existing = byConditionId.get(condition.cell_condition_id);
      const row: ConditionGroupRow = { group, workOrders };
      if (existing) {
        existing.rows.push(row);
      } else {
        byConditionId.set(condition.cell_condition_id, {
          condition,
          rows: [row],
        });
      }
    });
  });

  return Array.from(byConditionId.values());
};

/**
 * Group IDs that `conditionId` belongs to, derived from the raw work-orders
 * response. This is the many-to-many-correct source of truth for a condition's
 * group membership (replaces the stale singular condition field).
 */
export const getGroupIdsForCondition = (
  workOrders: Record<string, ConditionWorkOrderGroupResponse> | undefined,
  conditionId: number
): string[] => {
  if (!workOrders) {
    return [];
  }
  const conditionKey = String(conditionId);
  return Object.entries(workOrders)
    .filter(([, group]) => Boolean(group?.conditions?.[conditionKey]))
    .map(([groupId]) => groupId);
};

// ============================================================================
// Condition validations
// ============================================================================

type ConditionValidations = {
  isValidationsLoading: boolean;
  conditionHasErrors: (condition: FormspecCondition) => boolean;
};

/**
 * Fetch validations for all unsubmitted conditions. Reuses the per-uuid React
 * Query cache shared with GridView and Specification.tsx.
 */
export const useConditionValidations = (
  conditions: FormspecCondition[] | undefined,
  submittedConditionIds: Set<number>
): ConditionValidations => {
  const notSubmittedItemUuids = useMemo(() => {
    if (!conditions) return [];
    return conditions
      .filter((c) => !submittedConditionIds.has(c.cell_condition_id))
      .map((c) => (isItem(c.item) ? c.item.uuid : null))
      .filter((uuid): uuid is string => uuid !== null);
  }, [conditions, submittedConditionIds]);

  const validationQueries = useQueries({
    queries: notSubmittedItemUuids.map((uuid) => ({
      queryKey: [QueryKey.Items, uuid, "validation"],
      queryFn: async (): Promise<TemplateValidationResult> => {
        return await client.get(`formspec/items/${uuid}/validation`);
      },
      enabled: !!uuid,
      ...defaultQueryParams,
    })),
  });

  const isValidationsLoading = validationQueries.some((q) => q.isLoading);

  const validationsByUuid = useMemo(() => {
    const map = new Map<string, TemplateValidationResult>();
    validationQueries.forEach((query, index) => {
      const uuid = notSubmittedItemUuids[index];
      if (uuid && query.data) {
        map.set(uuid, query.data);
      }
    });
    return map;
  }, [validationQueries, notSubmittedItemUuids]);

  const conditionHasErrors = useCallback(
    (condition: FormspecCondition): boolean => {
      if (!isItem(condition.item)) return false;
      const validation = validationsByUuid.get(condition.item.uuid);
      return validation?.violations ? validation.violations.length > 0 : false;
    },
    [validationsByUuid]
  );

  return { isValidationsLoading, conditionHasErrors };
};
